<template>
  <HeadTitle
    title="Grupos de calendario"
    description="Lista de calendarios de un grupo de nóminas. Puedes agrupar calendarios por año"
  />
  <Toolset
    ref="toolset"
    :searcher="searcher"
    add-button-icon="pi pi-plus"
    add-button-label="Crear grupo de calendario"
    :to-add="toAdd"
    class="mt-5"
  />
  <DataTable
    class="tabla mb-5"
    :value="calendarGroups.data"
    paginator
    :rows="pagination.limit"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    tableStyle="min-width: 50rem"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    :totalRecords="calendarGroups.totalItems"
    :currentPageReportTemplate="currentPageReportTemplate"
    scrollable
    scrollHeight="600px"
    :loading="isLoading"
  >
    <template #empty> No se encontraron grupos de calendario. </template>
    <Column field="description" header="Descripción">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column field="onlyPayrolls" sortable header="Cant. Nóminas">
      <template #body="{ data, field }">
        {{ data[field].length }}
      </template>
    </Column>
    <Column field="holidays" sortable header="Cant. feriados">
      <template #body="{ data, field }">
        {{ data[field].length }}
      </template>
    </Column>
    <Column field="workingDaysIds" sortable header="Cant. días hábiles">
      <template #body="{ data, field }">
        {{ data[field].length }}
      </template>
    </Column>
    <Column field="updatedAt" sortable header="Ultima actualización">
      <template #body="{ data, field }">
        <span v-local-date="data[field]"></span>
      </template>
    </Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <ConfirmPopup></ConfirmPopup>
        <div class="actions">
          <Button
            v-tooltip.bottom="'Ver detalles'"
            class="action-btn view p-button-sm mr-2"
            icon="pi pi-eye"
            @click="goToDetails(data['id'])"
          />
          <Button
            class="action-btn view p-button-sm"
            icon="pi pi-trash"
            @click="onDeleteCalendarGroup($event, data['id'])"
            severity="danger"
            v-tooltip.bottom="'Eliminar'"
          />
        </div>
      </template>
    </Column>
  </DataTable>
  <Footer>
    <template #left>
      <AuditButton
        :entities="['CalendarGroup']"
        label="Ver registros eliminados"
        :root-id="payrollConfigId"
        only-deleted
      />
    </template>
    <Button
      label="Volver"
      severity="secondary"
      @click="router.push('/payrollsConfig/')"
      class="p-button-text text-sm font-light text-primary"
      icon="pi pi-angle-left"
    />
  </Footer>
</template>
<script setup lang="ts">
  import Toolset from '@/shared/components/Toolset.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import { useRoute } from 'vue-router'
  import {
    deleteCalendarGroup,
    getCalendarGroupsByPayrollConfigId,
  } from '../services/CalendarGroupService'
  import { computed, onMounted, reactive, ref } from 'vue'
  import CalendarGroup from '../models/CalendarGroup'
  import Footer from '@/shared/components/Footer.vue'
  import router from '@/router'
  import { useConfirm } from 'primevue/useconfirm'
  import useNotifications from '@/store/notification'

  const path = useRoute()
  const isLoading = ref(false)
  const confirm = useConfirm()
  const notificationStore = useNotifications()
  const payrollConfigId = ref()
  const toolset = ref()
  const calendarGroups = reactive({
    data: [] as CalendarGroup[],
    totalItems: 0,
  })

  const pagination = reactive({
    currentPage: 0,
    lastPage: 0,
    limit: 10,
  })

  onMounted(async () => {
    await refreshData()
  })

  const refreshData = async () => {
    isLoading.value = true
    const { id } = path.params
    payrollConfigId.value = id
    const response = await getCalendarGroupsByPayrollConfigId(String(id))
    calendarGroups.data = response.data
    calendarGroups.totalItems = response.totalItems
    isLoading.value = false
  }

  const searcher = async () => {
    const { id } = path.params
    const response = await getCalendarGroupsByPayrollConfigId(
      String(id),
      toolset.value.search
    )
    calendarGroups.data = response.data
    calendarGroups.totalItems = response.totalItems
  }

  const onDeleteCalendarGroup = async (event: any, id: string) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Realmente quieres eliminar este registro?',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'si',
      accept: async () => {
        const response = await deleteCalendarGroup(String(id))
        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `Grupo de documentos eliminado con exito.`,
          })
          await refreshData()
        }
      },
      reject: () => {},
    })
  }

  const goToDetails = async (id: string) => {
    const { id: payrollConfigId } = path.params
    router.push(
      '/payrollsConfig/' + payrollConfigId + '/calendarGroups/detail/' + id
    )
  }

  const toAdd = () => {
    const { id } = path.params
    router.push('/payrollsConfig/' + id + '/calendarGroups/add')
  }

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${calendarGroups.totalItems}`
  })
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  .actions {
    display: flex;
    width: auto;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }
</style>
